import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { GrBox, GrBreadcrumbs, GrSkeleton, GrStack, GrTypography, palette, typography } from "@jnpmedi/grebob";
import { useHistory } from "react-router-dom";
import { useLayoutContext } from "@newHooks/context/layout/layout.context";
import { ellipsis } from "@assets/styles/layout";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";

export type Breadcrumb = {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

type ContainerProps = {
  children: ReactNode;
  title?: { text: string; components?: ReactNode; textLoading?: boolean };
  breadcrumbs?: Breadcrumb[];
  onClickDefaultBreadcrumb?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  breadcrumbType?: "text" | "text-collapsed";
  excludeHomeBreadcrumb?: boolean;
};

export function Container({
  children,
  title,
  breadcrumbs: breadcrumbsVal,
  breadcrumbType,
  excludeHomeBreadcrumb,
  onClickDefaultBreadcrumb,
}: ContainerProps) {
  const history = useHistory();
  const { sidebarCollapsed, isHiddenSideBar } = useLayoutContext();

  const createBreadcrumb = (label: string, href?: string) => ({
    label,
    href,
    onClick:
      onClickDefaultBreadcrumb && href
        ? (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            onClickDefaultBreadcrumb?.(e);
            history.push(href);
          }
        : undefined,
  });

  const breadcrumbs: Breadcrumb[] = [...(excludeHomeBreadcrumb ? [] : [createBreadcrumb("Home")]), ...(breadcrumbsVal || [])];
  const isBreadcrumbs = !!breadcrumbs?.length;

  return (
    <ContainerSection sidebarCollapsed={sidebarCollapsed} isHiddenSideBar={isHiddenSideBar.value}>
      <ContainerWrapper flexDirection="column" width="100%" gap="8px">
        {isBreadcrumbs && (
          <StyledBreadcrumbBox>
            <GrBreadcrumbs size="small" items={breadcrumbs} type={breadcrumbType} />
          </StyledBreadcrumbBox>
        )}

        {!!title && (
          <GrStack justifyContent="space-between" alignItems="center" style={{ marginBottom: "8px" }}>
            {title.textLoading ? (
              <GrSkeleton height="24px" width="180px" />
            ) : (
              <GrTypography type="Content.Header03" color="grey100" style={ellipsis} title={title.text}>
                {title.text}
              </GrTypography>
            )}
            {title.components && <GrStack>{title.components}</GrStack>}
          </GrStack>
        )}

        {!!children && (
          <GrStack flexDirection="column" height="100%">
            {children}
          </GrStack>
        )}
      </ContainerWrapper>
    </ContainerSection>
  );
}

const ContainerSection = styled("section", {
  shouldForwardProp: (prop) => prop !== "isHiddenSideBar" && prop !== "sidebarCollapsed",
})<{
  isHiddenSideBar: boolean;
  sidebarCollapsed?: { value: boolean };
}>(({ isHiddenSideBar, sidebarCollapsed }) => ({
  display: "flex",
  position: "relative",

  marginLeft: isHiddenSideBar ? "0px" : sidebarCollapsed?.value ? baseWidth.appSidebarWidth : baseWidth.appSidebarWidthCollapsed,
  transition: "margin-left ease-in-out 0.23s",

  height: "100vh",
  minHeight: "100vh",

  backgroundColor: palette["bg020"],

  "@media (max-width: 1024px)": {
    marginLeft: isHiddenSideBar ? "0px" : baseWidth.appSidebarWidthCollapsed,
  },
}));

const ContainerWrapper = styled(GrStack)({
  position: "relative",
  zIndex: 1,

  height: `calc(100% - ${baseHeight.appHeaderHeight})`,
  minHeight: `calc(100% - ${baseHeight.appHeaderHeight})`,
  boxSizing: "border-box",

  marginTop: baseHeight.appHeaderHeight,

  padding: "20px 24px 16px !important",
});

const StyledBreadcrumbBox = styled(GrBox)({
  position: "sticky",
  zIndex: 1,
  top: baseHeight.appHeaderHeight,
  backgroundColor: palette["bg020"],
  ol: {
    lineHeight: 1,
  },
  span: {
    ...typography["Content.Subtitle04"],
  },

  ".MuiBreadcrumbs-li": {
    ...ellipsis,
  },
});
